import React, { useState, useEffect } from "react";
import { db } from "../../config/firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin } = useAuth();
  
  // Function to check if the current user can edit/delete an event
  const canModifyEvent = (event) => {
    // Admins can modify any event
    if (isAdmin) return true;
    // Gym admins can only modify their own events
    return event.createdBy === user?.uid;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      const eventsCollection = collection(db, "events");
      const eventsSnapshot = await getDocs(eventsCollection);
      const eventsList = eventsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by date descending (newest first)
      eventsList.sort((a, b) => {
        const dateA = parseLocalDate(a.date);
        const dateB = parseLocalDate(b.date);
        return dateB - dateA;
      });

      setEvents(eventsList);
      setLoading(false);
    };

    fetchEvents();
  }, []);

  // Helper function to parse date string without timezone conversion
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Separate upcoming and past events
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingEvents = events.filter(event => {
    const eventDate = parseLocalDate(event.date);
    return eventDate >= today;
  });

  const pastEvents = events.filter(event => {
    const eventDate = parseLocalDate(event.date);
    return eventDate < today;
  });

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      await deleteDoc(doc(db, "events", id));
      setEvents(events.filter((event) => event.id !== id));
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  const renderEventsTable = (eventsList, title, colorClass) => (
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        {title} ({eventsList.length})
      </h2>

      {eventsList.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No {title.toLowerCase()} at this time.</p>
      ) : (
        <>
          {/* Desktop/Tablet Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg min-w-full">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {eventsList.map((event) => (
                    <tr key={event.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {event.title}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {event.dateDisplay || event.date}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
                          {event.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {canModifyEvent(event) ? (
                          <>
                            <Link
                              to={`/admin/events/${event.id}`}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => handleDelete(event.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400">No permission</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile/Tablet Card View */}
          <div className="lg:hidden space-y-4">
            {eventsList.map((event) => (
              <div
                key={event.id}
                className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4"
              >
                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                  <div className="space-y-2">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 text-lg">
                      {event.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {event.dateDisplay || event.date}
                      </p>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colorClass}`}>
                        {event.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-4 pt-2 sm:pt-0 border-t sm:border-0 border-gray-200 dark:border-gray-700">
                    {canModifyEvent(event) ? (
                      <>
                        <Link
                          to={`/admin/events/${event.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(event.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <span className="text-gray-400">No permission</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Manage Events
        </h1>
        <Link
          to="/admin/events/new"
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add New Event
        </Link>
      </div>

      {/* Upcoming Events Section */}
      {renderEventsTable(upcomingEvents, "Upcoming Events", "bg-green-100 text-green-800")}

      {/* Past Events Section */}
      {renderEventsTable(pastEvents, "Past Events", "bg-gray-100 text-gray-800")}
    </div>
  );
};

export default EventsManager;
